import { getHeaders } from "@/shared";

import type {
  WishlistProduct,
  WishlistProductsPageResponse,
} from "../types/wishlist.type";

export type { WishlistProduct, WishlistProductsPageResponse };

// Every wishlist endpoint sits behind the API Gateway required_auth authorizer,
// which resolves the customer through login-adapter. That service validates
// X-Os against ios|android|web and 401s on the SITE/MSITE sales channel, so
// these requests must opt out of it. See api.helper.ts.
export const getWishlistHeaders = () => getHeaders({ osType: "web" });

export const wishlistPageQueryKey = (locale: string) => [
  "wishlist-products-page",
  locale,
];

export const getWishlistProductsPageQueryOptions = ({
  apiOrigin,
  locale,
  page = 1,
  size = 24,
}: {
  apiOrigin: string;
  locale: string;
  page?: number;
  size?: number;
}) => ({
  queryKey: ["wishlist-products-page", page, size, locale],
  queryFn: async (): Promise<WishlistProductsPageResponse> => {
    const response = await fetch(
      `${apiOrigin}/api/v1/wishlist/products?page=${page}&size=${size}`,
      {
        headers: getWishlistHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch wishlist products (${response.status})`);
    }

    return response.json();
  },
});

export const getWishlistInfiniteProductsQueryOptions = ({
  apiOrigin,
  size = 24,
}: {
  apiOrigin: string;
  size?: number;
}) => ({
  queryKey: ["wishlist-products-infinite", size],
  queryFn: async ({
    pageParam = 1,
  }: {
    pageParam?: number;
  }): Promise<WishlistProductsPageResponse> => {
    const response = await fetch(
      `${apiOrigin}/api/v1/wishlist/products?page=${pageParam}&size=${size}`,
      {
        headers: getWishlistHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch wishlist products (${response.status})`);
    }

    return response.json();
  },
});

export const removeWishlistProductMutationOptions = ({
  sku,
  apiOrigin,
  locale,
}: {
  sku: string;
  apiOrigin: string;
  locale: string;
}) => ({
  mutationFn: async () => {
    const response = await fetch(
      `${apiOrigin}/api/v1/wishlist/product/${sku}`,
      {
        method: "DELETE",
        headers: getWishlistHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to remove wishlist product");
    }

    return response.json();
  },
});

export type CheckWishlistedResponse = boolean;

export const checkIsWishlistedQueryOptions = ({
  sku,
  apiOrigin,
  locale,
}: {
  sku: string;
  apiOrigin: string;
  locale: string;
}) => ({
  queryKey: ["wishlist", sku, locale],
  queryFn: async (): Promise<CheckWishlistedResponse> => {
    const response = await fetch(
      `${apiOrigin}/api/v1/wishlist/product/${sku}`,
      {
        headers: getWishlistHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to check if product is wishlisted");
    }

    return response.json();
  },
});

export type ToggleWishlistResponse = { message: string; has_products: boolean };

export const toggleWishlistMutationOptions = ({
  sku,
  apiOrigin,
  isWishlisted,
  locale,
}: {
  sku: string;
  apiOrigin: string;
  isWishlisted: boolean;
  locale: string;
}) => ({
  mutationFn: async () => {
    let response = null;

    if (isWishlisted) {
      response = await fetch(`${apiOrigin}/api/v1/wishlist/product/${sku}`, {
        method: "DELETE",
        headers: getWishlistHeaders(),
      });
    } else {
      response = await fetch(`${apiOrigin}/api/v1/wishlist/product`, {
        method: "POST",
        body: JSON.stringify({ configurable_sku: sku }),
        headers: {
          "Content-Type": "application/json",
          ...getWishlistHeaders(),
        },
      });
    }

    if (!response.ok) {
      throw new Error("Failed to toggle wishlist");
    }

    return response.json() as Promise<ToggleWishlistResponse>;
  },
});
